import { FC, useState, useCallback, useEffect, useRef, useMemo } from "react";
import { 
  ReconciliationFormData, 
  ReconciliationStep, 
  ReconciliationInput,
  ReconciliationPreviewResponse
} from "@/types/reconciliation";
import {
  usePreviewReconciliationMutation,
  useProcessReconciliationMutation,
  useLazyGetDownloadStatusQuery,
  useLazyGetReportDownloadUrlQuery,
  useGetAllReportsQuery,
} from "@/services";
import { useAppSelector } from "@/store";
import { useSnackbar } from "@/hooks";
import { roles } from "@/roles";

import ReconciliationConfigModal from "../reconciliation-config";
import ReconciliationPreviewModal from "../reconciliation-preview";
import ReconciliationProgressModal from "../reconciliation-progress";

interface EnhancedReconciliationModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  onClose?: () => void;
}

const EnhancedReconciliationModal: FC<EnhancedReconciliationModalProps> = ({
  show,
  setShow,
  onClose,
}) => {
  const { show: showSnackbar } = useSnackbar();
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);

  // Role-based logic following main post-import page pattern
  const isTDAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN;
  }, [user, selectedTenant]);

  // Can select clients logic for defensive query handling
  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  // Defensive logic to prevent query when client context is missing
  const shouldSkip = useMemo(
    () => {
      if (isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
        return false; // Allow query to run for TDADMIN with TradeDive Client, even if selectedClient is not set
      }
      return canSelectClients && !selectedClient;
    },
    [canSelectClients, selectedClient, selectedTenant, user]
  );

  // Modal state management
  const [currentStep, setCurrentStep] = useState<ReconciliationStep>('configure');
  const [formData, setFormData] = useState<ReconciliationFormData | null>(null);
  const [previewData, setPreviewData] = useState<ReconciliationPreviewResponse | null>(null);
  
  // Processing state
  const [fileId, setFileId] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [processingStatus, setProcessingStatus] = useState<'PENDING' | 'SUCCESS' | 'FAILED'>('PENDING');
  const [processingError, setProcessingError] = useState<string | null>(null);
  
  // Ref to store polling interval for cleanup
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // API hooks
  const [previewReconciliation, { isLoading: isPreviewLoading, error: previewError }] = usePreviewReconciliationMutation();
  const [processReconciliation, { isLoading: isProcessing }] = useProcessReconciliationMutation();
  const [getDownloadStatus] = useLazyGetDownloadStatusQuery();
  const [getReportDownloadUrl] = useLazyGetReportDownloadUrlQuery();
  
  // Query to get reports for finding reportId by fileId - following main post-import page pattern
  const { data: reportsData, refetch } = useGetAllReportsQuery({
    // No reportType filter - get all reports to include VALUE_ADJUSTMENT and RATE_ADJUSTMENT
    pageSize: 50, // Get recent reports to find matching fileId
    clientId: selectedClient?._id,
    ...(isTDAdmin && { tenantId: selectedTenant?._id }),
  }, {
    refetchOnMountOrArgChange: true,
    skip: shouldSkip,
  });
  

  // Transform form data to API input format
  const transformToApiInput = useCallback((data: ReconciliationFormData): ReconciliationInput => {
    if (!selectedClient?._id) {
      throw new Error("No client selected");
    }

    // Check if any filters have actual values (not just empty arrays)
    const hasFilters = 
      data.filters.reconFlag || 
      (data.filters.htsCodes && data.filters.htsCodes.length > 0) ||
      (data.filters.originCodes && data.filters.originCodes.length > 0) ||
      (data.filters.manufacturerIds && data.filters.manufacturerIds.length > 0) ||
      (data.filters.entryLines && data.filters.entryLines.length > 0);

    return {
      date_range: {
        start: data.dateRange.start,
        end: data.dateRange.end,
      },
      filters: hasFilters
        ? {
            ...(data.filters.reconFlag && { ReconFlag: data.filters.reconFlag }),
            ...(data.filters.htsCodes?.length && { hts_codes: data.filters.htsCodes }),
            ...(data.filters.originCodes?.length && { origin_codes: data.filters.originCodes }),
            ...(data.filters.manufacturerIds?.length && { manufacturer_ids: data.filters.manufacturerIds }),
            ...(data.filters.entryLines?.length && { entry_lines: data.filters.entryLines }),
          }
        : undefined,
      adjustment: {
        type: data.adjustmentType,
        data: data.adjustmentData,
      },
      clientId: selectedClient._id,
      ...(isTDAdmin && selectedTenant?._id && { tenantId: selectedTenant._id }),
    };
  }, [selectedClient, isTDAdmin, selectedTenant]);

  // Handle configuration submission
  const handleConfigSubmit = async (data: ReconciliationFormData) => {
    try {
      setFormData(data);
      const apiInput = transformToApiInput(data);
      
      const result = await previewReconciliation(apiInput).unwrap();
      setPreviewData(result);
      setCurrentStep('preview');
    } catch (error: any) {
      console.error('Preview failed:', error);
      showSnackbar('Error', 'Failed to generate preview. Please check your configuration.', 'error');
    }
  };


  // Handle preview confirmation
  const handlePreviewConfirm = async () => {
    if (!formData) return;

    try {
      const apiInput = transformToApiInput(formData);
      const result = await processReconciliation(apiInput).unwrap();
      
      setFileId(result.fileId);
      setCurrentStep('processing');
      
      // Validate result format
      if (!result.fileId) {
        throw new Error('Invalid response: missing fileId');
      }

      // Start polling for progress if needed
      if (result.progressBar && result.fileId) {
        startPolling(result.fileId);
      } else if (result.signedUrl) {
        // Direct download available
        setDownloadUrl(result.signedUrl);
        setProcessingStatus('SUCCESS');
        setCurrentStep('complete');
      } else {
        // Neither progressBar nor signedUrl - this shouldn't happen
        console.warn('Unexpected response format: no progressBar or signedUrl', result);
        // Default to polling
        startPolling(result.fileId);
      }
    } catch (error: any) {
      console.error('Processing failed:', error);
      setProcessingError(error?.data?.message || 'Processing failed. Please try again.');
      setProcessingStatus('FAILED');
    }
  };

  // Helper function to find report ID by fileId
  const findReportIdByFileId = useCallback((targetFileId: string, data?: typeof reportsData): string | null => {
    // Use provided data or fall back to component state
    const dataToSearch = data || reportsData;
    
    if (!dataToSearch?.data) {
      console.error(`No reports data available for fileId lookup: ${targetFileId}`);
      return null;
    }
    
    // Search for the report with matching fileId
    const matchingReport = dataToSearch.data.find(report => report.fileId === targetFileId);
    
    if (matchingReport) {
      return matchingReport._id;
    }
    
    console.error(`No report found with fileId: ${targetFileId}`);
    return null;
  }, [reportsData]);

  // Start polling for download status
  const startPolling = async (fileId: string) => {
    try {
      const result = await getDownloadStatus(fileId).unwrap();
      
      if (result.status === 'SUCCESS') {
        // Clear polling interval since processing is complete
        clearPollingInterval();
        
        // Find the corresponding report ID
        // Refetch to ensure we have the latest data since Lambda just completed
        const refetchResult = await refetch();
        
        // Use the fresh data from refetch result, not the stale component state
        const freshData = refetchResult.data;
        const reportId = findReportIdByFileId(fileId, freshData);
        
        if (reportId) {
          // Generate presigned URL using the new system
          try {
            const downloadResult = await getReportDownloadUrl({ reportId }).unwrap();
            setDownloadUrl(downloadResult);
            setProcessingStatus('SUCCESS');
            setProgress(100);
            setCurrentStep('complete');
          } catch (urlError: any) {
            console.error('Failed to generate download URL:', urlError);
            setProcessingError('Failed to generate download URL. Please try again.');
            setProcessingStatus('FAILED');
          }
        } else {
          setProcessingError('Could not find report for download. Please try again.');
          setProcessingStatus('FAILED');
        }
      } else if (result.status === 'FAILED') {
        // Clear polling interval since processing failed
        clearPollingInterval();
        setProcessingError(result.msg || 'Processing failed');
        setProcessingStatus('FAILED');
      } else {
        // Still processing, update progress
        setProgress(result.progress || 0);
        setProcessingStatus('PENDING');
      }
    } catch (error: any) {
      console.error('Status check failed:', error);
      setProcessingError('Failed to check processing status');
      setProcessingStatus('FAILED');
    }
  };

  // Cleanup function for polling interval
  const clearPollingInterval = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Poll for status updates when file ID changes
  useEffect(() => {
    // Clear any existing interval first
    clearPollingInterval();
    
    if (fileId && processingStatus === 'PENDING') {
      pollingIntervalRef.current = setInterval(() => {
        startPolling(fileId);
      }, 3000);
    }

    return clearPollingInterval;
  }, [fileId, processingStatus, clearPollingInterval]);

  // Clean up when modal is closed
  useEffect(() => {
    if (!show) {
      // Clear polling and reset state
      clearPollingInterval();
      setProcessingStatus('PENDING');
      setFileId(null);
      setDownloadUrl(null);
      setProgress(0);
      setProcessingError(null);
    }
  }, [show, clearPollingInterval]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      clearPollingInterval();
    };
  }, [clearPollingInterval]);

  // Handle download
  const handleDownload = async (url: string) => {
    try {
      // Open the presigned URL in a new tab for download
      window.open(url, '_blank');
      showSnackbar('Success', 'Download started successfully!', 'success');
    } catch (error) {
      console.error('Download failed:', error);
      showSnackbar('Error', 'Failed to download file. Please try again.', 'error');
    }
  };


  // Handle modal close
  const handleClose = () => {
    setShow(false);
    
    // Call onClose callback to trigger reports refetch
    if (onClose) {
      onClose();
    }
    
    // Reset state after a short delay to avoid visual glitches
    setTimeout(() => {
      setCurrentStep('configure');
      setFormData(null);
      setPreviewData(null);
      setFileId(null);
      setDownloadUrl(null);
      setProgress(0);
      setProcessingStatus('PENDING');
      setProcessingError(null);
    }, 300);
  };

  // Determine which modal to show
  const showConfigModal = show && currentStep === 'configure';
  const showPreviewModal = show && currentStep === 'preview' && formData !== null;
  const showProgressModal = show && (currentStep === 'processing' || currentStep === 'complete');

  return (
    <>
      <ReconciliationConfigModal
        show={showConfigModal}
        setShow={(show) => show ? setShow(true) : handleClose()}
        onSubmit={handleConfigSubmit}
        isLoading={isPreviewLoading}
      />
      
      <ReconciliationPreviewModal
        show={showPreviewModal}
        setShow={(show) => show ? setShow(true) : handleClose()}
        onConfirm={handlePreviewConfirm}
        previewData={previewData || undefined}
        formData={formData || undefined}
        isLoading={isPreviewLoading}
        isProcessing={isProcessing}
        error={previewError ? 'Failed to generate preview' : undefined}
      />
      
      <ReconciliationProgressModal
        show={showProgressModal}
        setShow={(show) => show ? setShow(true) : handleClose()}
        onDownload={handleDownload}
        onClose={handleClose}
        fileId={fileId || undefined}
        status={processingStatus}
        progress={progress}
        downloadUrl={downloadUrl || undefined}
        error={processingError || undefined}
        adjustmentType={formData?.adjustmentType}
      />
    </>
  );
};

export default EnhancedReconciliationModal;